import emailjs from "@emailjs/browser";
import i18n from "../i18n"; // Import i18n to access current language

// Initialize EmailJS with your user ID
const initializeEmailJS = () => {
  const userID = process.env.REACT_APP_EMAILJS_USER_ID;
  if (userID) {
    emailjs.init(userID);
  } else {
    console.error("EmailJS User ID not found in environment variables");
  }
};

// Interface for form data
export interface FormData {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  services: { [key: string]: boolean };
  specialRequest: string;
  marketingConsent: boolean;
}

/** Optional EmailJS fields: lead_source / merged special_request when from /contactfb */
export interface SendConfirmationEmailOptions {
  leadSourceLabel?: string;
}

// Send confirmation email to client
export const sendClientConfirmationEmail = async (
  formData: FormData,
  options?: SendConfirmationEmailOptions
): Promise<boolean> => {
  try {
    initializeEmailJS();

    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;

    // Determine which template to use based on current language
    const currentLanguage = i18n.language || "en";
    const isFrench = currentLanguage.startsWith("fr");

    // Use different template IDs based on language
    const templateID = isFrench
      ? process.env.REACT_APP_EMAILJS_TEMPLATE_ID_FR // French template
      : process.env.REACT_APP_EMAILJS_TEMPLATE_ID; // English template

    if (!serviceID || !templateID) {
      console.error("EmailJS configuration missing", {
        serviceID: !!serviceID,
        templateID: !!templateID,
        language: currentLanguage,
        isFrench,
      });
      return false;
    }

    console.log(
      `Sending email in ${
        isFrench ? "French" : "English"
      } (language: ${currentLanguage})`
    );

    // Format selected services for the email
    const selectedServices = Object.entries(formData.services)
      .filter(([_, isSelected]) => isSelected)
      .map(([serviceName]) => {
        return serviceName
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      })
      .join(", ");

    const leadSource = options?.leadSourceLabel?.trim() || "";
    const userNotes = formData.specialRequest?.trim();
    const special_request = leadSource
      ? userNotes
        ? `${leadSource}\n\n${userNotes}`
        : leadSource
      : userNotes || "None";

    // Template parameters for EmailJS (lead_source for dedicated line; special_request includes source when set)
    const templateParams = {
      to_email: formData.email, // For EmailJS "To Email" field
      to_name: formData.fullName, // For EmailJS "To Name" field
      client_name: formData.fullName,
      client_email: formData.email, // Alternative recipient field
      client_phone: formData.phone,
      client_address: formData.address,
      client_postal_code: formData.postalCode,
      selected_services: selectedServices,
      special_request,
      lead_source: leadSource,
      business_phone: process.env.REACT_APP_BUSINESS_PHONE || "514-979-8020",
      business_email:
        process.env.REACT_APP_BUSINESS_EMAIL || "info@bearzlandscaping.com",
    };

    const result = await emailjs.send(serviceID, templateID, templateParams);

    if (result.status >= 200 && result.status < 300) {
      console.log("Confirmation email sent successfully");
      return true;
    } else {
      console.error("Failed to send confirmation email:", result);
      return false;
    }
  } catch (error) {
    console.error("Error sending confirmation email:", error);
    return false;
  }
};

// Send notification email to business (optional - you can set up a separate template for this)
export const sendBusinessNotificationEmail = async (
  formData: FormData,
  options?: SendConfirmationEmailOptions
): Promise<boolean> => {
  try {
    initializeEmailJS();

    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    // You can create a separate template ID for business notifications
    const businessTemplateID =
      process.env.REACT_APP_EMAILJS_BUSINESS_TEMPLATE_ID;

    if (!serviceID || !businessTemplateID) {
      console.log("Business notification template not configured, skipping...");
      return true; // Return true to not block the main flow
    }

    const selectedServices = Object.entries(formData.services)
      .filter(([_, isSelected]) => isSelected)
      .map(([serviceName]) => {
        return serviceName
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      })
      .join(", ");

    const leadSource = options?.leadSourceLabel?.trim() || "";
    const userNotes = formData.specialRequest?.trim();
    const special_request = leadSource
      ? userNotes
        ? `${leadSource}\n\n${userNotes}`
        : leadSource
      : userNotes || "None";

    const templateParams = {
      to_email:
        process.env.REACT_APP_BUSINESS_EMAIL || "info@bearzlandscaping.com",
      client_name: formData.fullName,
      client_email: formData.email,
      client_phone: formData.phone,
      client_address: formData.address,
      client_postal_code: formData.postalCode,
      selected_services: selectedServices,
      special_request,
      lead_source: leadSource,
    };

    const result = await emailjs.send(
      serviceID,
      businessTemplateID,
      templateParams
    );

    if (result.status >= 200 && result.status < 300) {
      console.log("Business notification email sent successfully");
      return true;
    } else {
      console.error("Failed to send business notification email:", result);
      return false;
    }
  } catch (error) {
    console.error("Error sending business notification email:", error);
    return false;
  }
};
